import React, { useMemo, useState, type ComponentProps } from 'react';
import { get } from 'lodash';
import { excludeChildrenByType, findChildByType, getComponentProps } from '@fiverr-private/ui_utils';
import { Box } from '@fiverr-private/theme';
import { translate } from '@fiverr-private/i18n-react';
import { Center } from '@fiverr-private/layout_components';
import type { AvatarProps } from './types';
import { getAvatarBackgroundColor, getBorderRadius } from './utils';
import { AvatarContext } from './AvatarContext';
import { AI_RING_BORDER_WIDTH, AVATAR_SIZES, FONT_SIZES, RING_BORDER_WIDTH } from './constants';
import { getRingStyles } from './styles';
import AvatarAnimation from './AvatarAnimation';
import { ai_zoom_animation } from './styles.ve.css';

const Avatar = ({
  username,
  displayName,
  size = 'xs',
  shape = 'circle',
  color = 'auto',
  showTitle = true,
  onClick,
  children,
  hidden,
  showRing,
  showBorder = true,
  dataTestId,
  isLoading,
  dataTrackTag = 'avatar',
  dataTrackValue = username,
}: AvatarProps) => {
  const nameValue = displayName || username;
  const firstLetter = get(nameValue, '[0]', '');
  const isOnClickValid = typeof onClick === 'function';
  const baseAvatarSize = AVATAR_SIZES[size];
  const avatarImage = findChildByType(children, 'AvatarImage');
  const avatarVideo = findChildByType(children, 'AvatarVideo');
  const sparkSolidIcon = findChildByType(children, 'SparkSolidIcon');
  const { autoPlay } = getComponentProps(avatarVideo);
  const [videoAutoPlay, setVideoAutoPlay] = useState<boolean>(autoPlay);
  const borderRadius = getBorderRadius(shape, size);
  const hasVideo = Boolean(avatarVideo);
  const isAiAvatar = Boolean(sparkSolidIcon);
  const aiAvatarLoading = isAiAvatar && isLoading;
  const hasAvatarAnimation = Boolean(avatarVideo) || aiAvatarLoading;
  const { fontColor, backgroundColor } = useMemo(
    () => getAvatarBackgroundColor({ color, username, isAiAvatar }),
    [color, isAiAvatar, username]
  );

  const avatarSize = useMemo(() => {
    if (showRing || hasVideo) {
      return baseAvatarSize - RING_BORDER_WIDTH;
    }
    if (isLoading) {
      return baseAvatarSize - AI_RING_BORDER_WIDTH;
    }
    return baseAvatarSize;
  }, [showRing, hasVideo, isLoading, baseAvatarSize]);
  const borderProperties: ComponentProps<typeof Box> = showBorder
    ? {
        borderColor: 'border',
        borderWidth: 'sm',
        borderStyle: 'solid',
      }
    : {};

  return (
    <AvatarContext.Provider
      value={{
        size,
        actualAvatarSize: avatarSize,
        nameValue,
        shape,
        setVideoAutoPlay,
        videoAutoPlay,
      }}
    >
      <Box
        onClick={onClick}
        role={isOnClickValid ? 'button' : undefined}
        aria-label={isOnClickValid ? translate<string>('avatar.avatar.figure') : undefined}
        hidden={hidden}
        data-testid={dataTestId}
        position="relative"
        justifyContent="center"
        alignItems="center"
        display="inlineFlex"
        boxSizing="borderBox"
        width={baseAvatarSize}
        height={baseAvatarSize}
        {...(showRing && !hasAvatarAnimation ? getRingStyles({ shape, borderRadius }) : {})}
        dataTrackTag={dataTrackTag}
        dataTrackValue={dataTrackValue}
      >
        {hasAvatarAnimation && (
          <AvatarAnimation
            autoPlay={videoAutoPlay}
            borderRadius={borderRadius}
            shape={shape}
            baseAvatarSize={baseAvatarSize}
            isLoading={aiAvatarLoading}
          />
        )}
        <Box
          as="figure"
          margin="0"
          display="inlineFlex"
          justifyContent="center"
          alignItems="center"
          position="relative"
          width={avatarSize}
          height={avatarSize}
          boxSizing="borderBox"
          overflow="hidden"
          backgroundColor={avatarImage === null && avatarVideo === null ? 'transparent' : backgroundColor}
          borderRadius={borderRadius}
          fontSize={FONT_SIZES[size]}
          lineHeight={FONT_SIZES[size]}
          transition={isAiAvatar ? 'width 0.2s, height 0.2s' : undefined}
          title={showTitle ? nameValue : ''}
          {...borderProperties}
        >
          {!isAiAvatar && (
            <Box
              as="figcaption"
              fontWeight="semibold"
              fontSize={FONT_SIZES[size]}
              lineHeight={FONT_SIZES[size]}
              color={fontColor}
            >
              {firstLetter.toLocaleUpperCase()}
            </Box>
          )}
          {avatarImage}
          {avatarVideo}
          {sparkSolidIcon && (
            <Center className={aiAvatarLoading ? ai_zoom_animation : undefined}>{sparkSolidIcon}</Center>
          )}
        </Box>
        {excludeChildrenByType(children, ['AvatarImage', 'AvatarVideo', 'SparkSolidIcon'])}
      </Box>
    </AvatarContext.Provider>
  );
};

Avatar.id = 'Avatar';
Avatar.displayName = 'Avatar';

export default Avatar;
